import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Chip, Paper, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { UpsertJustifyDelayModal } from "@/pages/delays/manager/components/modals/UpsertJustifyDelayModal";
import { SplitDelaysModal } from "@/pages/delays/manager/components/modals/SplitDelaysModal";
import { DELAYS_QUERY_KEYS } from "@/pages/delays/manager/hooks/useDelays";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { useCallback, useEffect, useRef, useState } from "react";
import { REFRESH_INTERVAL } from "../../models/constants";
import { getLastBilletsData } from "../../repositories/MillSetupRepository";
import type { LastBillet } from "../../models/CampaignData";

interface MillSetupDelayModalProps {
	showModal: "justify" | "";
	onHide: (shouldUpdate: boolean) => void;
	delayId: number | null;
	delayStartTimestamp: string | null;
}

/**
 * Wrapper around UpsertJustifyDelayModal that adds:
 * - Last 3 billets context (fetched on open)
 * - Split delay support
 * - Live update every 5s while modal is open
 * Mill-setup-specific behavior matching legacy rolling-mill-setup.js
 */
export const MillSetupDelayModal = ({
	showModal,
	onHide,
	delayId,
	delayStartTimestamp,
}: MillSetupDelayModalProps) => {
	const [splitDelayId, setSplitDelayId] = useState<number | null>(null);
	const queryClient = useQueryClient();
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	// Live update: refetch delay data every 5s while modal is open (#9)
	// Guard: don't refetch if split mode is active
	useEffect(() => {
		if (showModal === "justify" && delayId && !splitDelayId) {
			intervalRef.current = setInterval(() => {
				queryClient.invalidateQueries({
					queryKey: DELAYS_QUERY_KEYS.byId(delayId),
				});
			}, REFRESH_INTERVAL);
		}

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [showModal, delayId, splitDelayId, queryClient]);

	// Fetch last billets when modal opens
	const { data: lastBillets = [] } = useQuery({
		queryKey: ["lastBillets", delayStartTimestamp],
		queryFn: () => getLastBilletsData(delayStartTimestamp!),
		enabled: showModal === "justify" && delayStartTimestamp != null,
	});

	const handleSplit = useCallback(
		(id: number) => {
			onHide(false); // close justify modal
			setSplitDelayId(id); // open split modal
		},
		[onHide],
	);

	const handleSplitHide = useCallback(
		(shouldUpdate: boolean) => {
			setSplitDelayId(null);
			if (shouldUpdate) {
				onHide(true);
			}
		},
		[onHide],
	);

	return (
		<>
			{/* Last billets context — banner above the modal backdrop */}
			{showModal === "justify" && lastBillets.length > 0 && (
				<LastBilletsBanner billets={lastBillets} />
			)}

			<UpsertJustifyDelayModal
				showModal={showModal}
				onHide={onHide}
				delayId={delayId}
				suffixTitle="Mill Setup"
				onSplit={handleSplit}
			/>

			{/* Split Delay Modal */}
			<SplitDelaysModal
				show={!!splitDelayId}
				onHide={handleSplitHide}
				delayId={splitDelayId}
				suffixTitle="Mill Setup"
			/>
		</>
	);
};

/** Banner showing last 3 billets context — anchored to top-center above the modal */
const LastBilletsBanner = ({ billets }: { billets: LastBillet[] }) => (
	<Paper
		elevation={6}
		sx={{
			position: "fixed",
			top: 8,
			left: "50%",
			transform: "translateX(-50%)",
			zIndex: 1400,
			px: 2.5,
			py: 1,
			display: "flex",
			alignItems: "center",
			gap: 2,
			borderRadius: 1,
			bgcolor: "#fff8e1",
			border: "1px solid #ffe082",
			maxWidth: "90vw",
		}}
	>
		<Typography
			sx={{
				fontSize: 12,
				fontWeight: 700,
				color: "#1e293b",
				whiteSpace: "nowrap",
			}}
		>
			Last Billets:
		</Typography>
		{billets.map((b) => (
			<Box
				key={b.Id}
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 0.75,
				}}
			>
				<Typography sx={{ fontSize: 12, fontWeight: 600 }}>#{b.Id}</Typography>
				<Typography sx={{ fontSize: 12 }} noWrap>
					{b.Name}
				</Typography>
				{b.StartString && (
					<Typography sx={{ fontSize: 11, color: "#888" }}>
						{dxtToLocalServerTime(b.StartString, "HH:mm:ss")}
					</Typography>
				)}
				{b.IsCobble && (
					<Chip
						icon={<WarningAmberIcon sx={{ fontSize: 12 }} />}
						label="Cobble"
						size="small"
						color="error"
						sx={{ height: 18, fontSize: 10, "& .MuiChip-icon": { ml: 0.25 } }}
					/>
				)}
			</Box>
		))}
	</Paper>
);
